import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MarkdownBody } from '../knowledge/Markdown';
import Loader from '../components/Loader';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { useTranslation } from 'react-i18next';
import { KRAJ, jestPolska } from '../kraj';

// Prosta strona tekstowa (regulamin / polityka) — treść z pliku .md w public/legal.
// Białe tło, czytelna szerokość, bez ozdobników. Navbar i footer są globalne (App.js).
// Wersja krajowa czyta tłumaczenie z public/legal/<kod kraju>/ (np. legal/ro/regulamin.md).
// Gdy go nie ma, pokazujemy polski oryginał. Serwer na brakujący plik odpowiada
// index.html (fallback SPA) z kodem 200, dlatego oprócz `ok` sprawdzamy też typ treści.
const pobierzTekst = async (file) => {
  if (!jestPolska) {
    try {
      const res = await fetch(`${process.env.PUBLIC_URL}/legal/${KRAJ.kod}/${file}`);
      const typ = res.headers.get('content-type') || '';
      if (res.ok && !typ.includes('text/html')) return res.text();
    } catch (e) {
      // brak tłumaczenia: niżej polski plik
    }
  }
  const res = await fetch(`${process.env.PUBLIC_URL}/legal/${file}`);
  return res.text();
};

const LegalPage = ({ file, fallbackTitle: tytulZRoutingu }) => {
  const { t } = useTranslation('wspolne');
  // Tytuł z routingu jest po polsku; w słowniku `wspolne` (prawne.tytul) ma tłumaczenie.
  // Klucz to nazwa pliku bez `.md`. W Polsce wartość jest identyczna z dotychczasową.
  const fallbackTitle = t(`prawne.tytul.${file.replace(/\.md$/, '')}`, { defaultValue: tytulZRoutingu });
  const bladWczytania = t('prawne.blad_wczytania');
  const [state, setState] = useState({ loading: true, title: '', body: '' });
  // Krótka nazwa z routingu, NIE nagłówek z pliku .md: ten zawiera już markę
  // („Regulamin korzystania z serwisu VoltValue"), a hook dokłada „| VoltValue",
  // więc w zakładce wychodziła nazwa serwisu dwa razy.
  useDocumentTitle(fallbackTitle || state.title);
  const { pathname, hash } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;
    const load = async () => {
      try {
        const raw = await pobierzTekst(file);
        const lines = raw.replace(/\r\n/g, '\n').split('\n');
        let title = fallbackTitle;
        let titleTaken = false;
        const kept = [];
        for (const line of lines) {
          if (!titleTaken && /^#\s+/.test(line)) {
            title = line.replace(/^#\s+/, '').trim();
            titleTaken = true;
            continue;
          }
          // pomiń poziome separatory --- / *** (renderer ich nie obsługuje)
          if (/^\s*---\s*$/.test(line) || /^\s*\*\*\*\s*$/.test(line)) continue;
          kept.push(line);
        }
        if (!cancelled) setState({ loading: false, title, body: kept.join('\n') });
      } catch (e) {
        if (!cancelled) setState({ loading: false, title: fallbackTitle, body: bladWczytania });
      }
    };
    load();
    return () => { cancelled = true; };
  }, [file, fallbackTitle, bladWczytania, pathname]);

  // Kotwica w adresie (np. `#8-pliki-cookies` z linku w stopce). Treść wczytuje się
  // asynchronicznie, więc przeglądarka sama nie przewinie: nagłówka jeszcze nie ma.
  // Przewijamy po wczytaniu i przy każdej zmianie kotwicy na tej samej stronie.
  useEffect(() => {
    if (state.loading || !hash) return;
    const el = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (el) el.scrollIntoView();
  }, [state.loading, hash]);

  if (state.loading) {
    return (<div className='fixed inset-0 flex justify-center items-center'><Loader /></div>);
  }

  return (
    <div className='bg-white'>
      <div className='max-w-3xl mx-auto px-4 md:px-6 py-10'>
        <h1 className='text-2xl md:text-3xl font-bold text-gray-900 mb-8'>{state.title}</h1>
        <MarkdownBody body={state.body} />
      </div>
    </div>
  );
};

export default LegalPage;
